import { fmtPrice as quoteMoney42 } from '@/lib/api';
import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import PayAnotherWayOption from '@/components/PayAnotherWayOption';
import { handleStripeDecline } from '@/lib/paymentDecline';
import { PaymentDeclineNotice } from '@/components/PaymentDeclineNotice';

/**
 * PaymentStep
 * -----------
 * Stripe Elements-powered checkout step in the parent registration flow.
 *
 * Props:
 *   coachSlug: string  (required) — drives the connected-account context
 *   coachDisplayName: string — shown verbatim in the consent disclosure
 *   pkg: {id, name, billing_type, price_cents}  (required)
 *   parent: {email, first_name, last_name, phone}  (required for public flow)
 *   onSuccess(result): callback fired AFTER Stripe confirms. Receives
 *      {intent_type, payment_intent_id|setup_intent_id, payment_method_id}
 *      so the caller can store it on the parent profile / enrollment.
 *   publishableKey: string — Stripe publishable key for the platform (NOT
 *      the secret). We pass `stripeAccount` to scope to the coach.
 *
 * Three paths:
 *   - billing_type=one_time          → PaymentIntent (charged now)
 *   - billing_type=recurring_monthly → SetupIntent now, server creates
 *                                       Subscription post-confirm
 *   - billing_type=per_lesson        → SetupIntent now, no charge today
 *
 * Card-on-file behaviour (Phase 0bv, 2026-08 — mandatory card-on-file):
 *   Every parent payment on SplashPad auto-saves the card for future
 *   off-session charges by the coach. No opt-in checkbox anymore.
 *   Instead we render a locked consent disclosure line above the pay
 *   button — Stripe requires this text for `setup_future_usage='off_session'`
 *   to hold up in disputes. Parents can manage their saved cards from
 *   the /parent/payment-methods page.
 */

export function PaymentStep({ coachSlug, coachDisplayName, pkg, parent, onSuccess, publishableKey, payAnotherWay, onExternal }) {
    const { t: t42 } = useLocaleR42();
    const [initData, setInitData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        api.post(`/public/coach/${coachSlug}/checkout/init`, {
            package_id: pkg.id,
            parent_email: parent.email,
            parent_first_name: parent.first_name,
            parent_last_name: parent.last_name,
            parent_phone: parent.phone || '',
            // Phase 0bv (2026-08) — the `save_card` field is deprecated
            // (server-side always saves) but kept for backwards API compat.
            save_card: true,
        })
            .then((r) => { if (alive) setInitData(r.data); })
            .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not start checkout'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // parent.first_name / parent.last_name / parent.phone are passed
        // to Stripe but don't gate the init contract — we deliberately
        // exclude them from deps to avoid creating a new PaymentIntent
        // every time the parent edits a character of their name.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coachSlug, pkg.id, parent.email]);

    // Memoize the Stripe promise scoped to the coach's connected account.
    // This is THE critical part of Connect: the publishable key is for the
    // platform, but `stripeAccount` routes the Elements UI to the coach.
    const stripePromise = useMemo(() => {
        if (!publishableKey || !initData?.stripe_account_id) return null;
        return loadStripe(publishableKey, { stripeAccount: initData.stripe_account_id });
    }, [publishableKey, initData?.stripe_account_id]);

    if (loading) {
        return (
            <Card className="sd-card flex items-center gap-2 p-6 text-sm text-[#1a3a5c]/70" data-testid="paystep-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> {t42("Setting up checkout…")} </Card>
        );
    }
    if (error) {
        return (
            <Card className="sd-card border-[#ff6b35]/40 p-6" data-testid="paystep-error">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#ff6b35]" />
                    <div>
                        <div className="font-semibold text-[#0a1628]">{t42("Couldn't start checkout")}</div>
                        <p className="mt-1 text-sm text-[#1a3a5c]">{error}</p>
                    </div>
                </div>
            </Card>
        );
    }
    if (!stripePromise || !initData?.client_secret) {
        return (
            <Card className="sd-card p-6" data-testid="paystep-not-ready">
                <p className="text-sm text-[#1a3a5c]">{t42("Payment is not available right now. Please try again later.")}</p>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            <Elements
                stripe={stripePromise}
                options={{
                    clientSecret: initData.client_secret,
                    appearance: {
                        theme: 'stripe',
                        variables: {
                            colorPrimary: '#00d4ff',
                            colorBackground: '#ffffff',
                            colorText: '#0a1628',
                            colorDanger: '#ff6b35',
                            fontFamily: 'Figtree, system-ui, sans-serif',
                            borderRadius: '12px',
                        },
                    },
                }}
            >
                <PaymentInner
                    initData={initData}
                    pkg={pkg}
                    onSuccess={onSuccess}
                    coachDisplayName={coachDisplayName}
                    coachSlug={coachSlug}
                    parent={parent}
                    onPayAnotherWay={payAnotherWay?.enabled ? () => onExternal?.({ amount_cents: pkg?.price_cents || 0, label: pkg?.name || 'Package' }) : undefined}
                />
            </Elements>

            {/* Phase 99 — offline payment (Venmo/Zelle/cash). Only rendered
                when the coach opted in AND the caller confirmed this is NOT
                a class package (classes always pay by card). The enrollment
                lands at payment_pending_external until the coach marks it
                paid — the spot is NOT confirmed at checkout. */}
            {payAnotherWay?.enabled && (
                <PayAnotherWayOption
                    idPrefix="paystep"
                    instructions={payAnotherWay.instructions}
                    coachDisplayName={coachDisplayName}
                    onConfirm={() => onExternal?.({
                        amount_cents: pkg?.price_cents || 0,
                        label: pkg?.name || 'Package',
                    })}
                />
            )}
        </div>
    );
}

function PaymentInner({ initData, pkg, onSuccess, coachDisplayName, coachSlug, parent, onPayAnotherWay }) {
    const { t: t42 } = useLocaleR42();
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errMsg, setErrMsg] = useState('');
    const [decline, setDecline] = useState(null);

    // Phase 178 — let the parent enter a different card without reloading.
    const tryAnotherCard = () => {
        setDecline(null);
        setErrMsg('');
        try { elements?.getElement('payment')?.clear(); } catch { /* older Elements */ }
        try { elements?.getElement('payment')?.focus(); } catch { /* noop */ }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setErrMsg('');
        setDecline(null);

        const isSetup = initData.intent_type === 'setup_intent';
        const confirmFn = isSetup ? stripe.confirmSetup : stripe.confirmPayment;
        const result = await confirmFn({
            elements,
            confirmParams: {
                // We stay in-app — no redirect-based payment methods (we
                // only enabled card, but `if_required` keeps us robust).
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (result.error) {
            // Phase 178 — plain-English reason + report to the coach.
            setDecline(handleStripeDecline(result.error, {
                context: 'register',
                coachSlug,
                amountCents: pkg?.price_cents || 0,
                packageName: pkg?.name,
                parentEmail: parent?.email,
                parentName: [parent?.first_name, parent?.last_name].filter(Boolean).join(' ') || undefined,
                paymentIntentId: initData?.intent_id || initData?.payment_intent_id,
            }));
            setSubmitting(false);
            return;
        }

        // Surface intent IDs to the caller — they'll attach to the
        // application/enrollment record server-side.
        const intent = result.paymentIntent || result.setupIntent;
        onSuccess({
            intent_type: initData.intent_type,
            intent_id: intent?.id,
            payment_method_id: intent?.payment_method,
            status: intent?.status,
            billing_type: initData.billing_type,
        });
        setSubmitting(false);
    };

    const amount = initData.amount_cents ?? pkg.price_cents ?? 0;
    const currency = initData.currency || pkg.currency || 'usd';
    const isOneTime = initData.billing_type === 'one_time';
    const isRecurring = initData.billing_type === 'recurring_monthly';
    const isPerLesson = initData.billing_type === 'per_lesson';

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="paystep-form">
            {/* Payment summary line */}
            <Card className="sd-card p-4" data-testid="paystep-summary">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div>
                        <div className="font-semibold text-[#0a1628]">{pkg.name}</div>
                        <div className="text-xs text-[#1a3a5c]/70">
                            {isOneTime && 'One-time charge today'}
                            {isRecurring && `${quoteMoney42(amount, currency)}/month, starting on the coach's configured billing day`}
                            {isPerLesson && 'No charge today — your coach will bill you per lesson'}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase tracking-wide text-[#1a3a5c]/60">
                            {isPerLesson ? t42("Card on file only") : isRecurring ? t42("Per month") : t42("Total")}
                        </div>
                        <div className="font-display text-2xl font-bold text-[#0a1628]">
                            {isPerLesson ? quoteMoney42(0, currency) : `${quoteMoney42(amount, currency)}`}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Stripe-hosted card form */}
            <Card className="sd-card p-4">
                <PaymentElement options={{ layout: 'tabs' }} />
            </Card>

            {/* Card-on-file consent disclosure (Phase 0bv — mandatory).
                Locked, not a toggle. Stripe REQUIRES this language for
                setup_future_usage='off_session' to hold up in disputes. */}
            <div
                className="flex items-start gap-3 rounded-xl border border-[#00d4ff]/25 bg-[#e6f9fd] p-4 text-sm text-[#0a1628]"
                data-testid="paystep-card-on-file-notice"
            >
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" aria-hidden="true" />
                <span className="leading-relaxed">
                    <strong>{t42("Your card will be saved on file.")}</strong>{' '}
                    <span className="text-[#1a3a5c]/85"> {t42("By paying, you authorize")} {coachDisplayName || 'your coach'} {t42("to save this card and charge it for future lessons or packages. You can manage your saved cards anytime from")}{' '}
                        <span className="font-medium">{t42("Payment methods")}</span> {t42("in your parent portal.")} </span>
                </span>
            </div>

            {decline && (
                <PaymentDeclineNotice
                    decline={decline}
                    idPrefix="paystep-decline"
                    onTryAnother={tryAnotherCard}
                    onRetry={() => { setDecline(null); }}
                    onPayAnotherWay={onPayAnotherWay}
                />
            )}
            {errMsg && !decline && (
                <div
                    className="rounded-xl border border-[#ff6b35]/40 bg-[#fff5f0] p-3 text-sm text-[#ff6b35]"
                    data-testid="paystep-error-msg"
                >
                    {errMsg}
                </div>
            )}

            <Button
                type="submit"
                disabled={!stripe || submitting}
                className="sd-cta sd-focus h-12 w-full rounded-xl text-base"
                data-testid="paystep-submit-button"
            >
                {submitting
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t42("Processing…")}</>
                    : isPerLesson || isRecurring
                        ? <><CheckCircle2 className="mr-2 h-4 w-4" /> {t42("Save card & continue")}</>
                        : <><CheckCircle2 className="mr-2 h-4 w-4" /> {t42("Pay")} {quoteMoney42(amount, currency)}</>}
            </Button>

            <p className="text-center text-xs text-[#1a3a5c]/60">
                <Lock className="mr-1 inline h-3 w-3" /> {t42("Secured by Stripe. SplashPad takes a small platform fee — your coach receives the rest on their next payout.")} </p>
        </form>
    );
}

export default PaymentStep;
